/**
 * Product Reviews Widget
 *
 * Renders product reviews with sorting, filtering, and pagination.
 * Data is fetched from the provider module via the importmap.
 */

import { fetchReviews, fetchBottomLine, fetchQuestions, fetchQuestionCount, createReview, createQuestion, fetchUGCImages } from '@stardust/yotpo';
import { renderStars, formatScore, pluralizeReviews, STAR_COLOR_CLASS } from '@stardust/pdp-reviews';
import { escapeHtml, formatLongDate, formatShortDate, renderPlayBadge, whenNearViewport } from '@stardust/utilities';
// Side-effect imports: register the two mobile bottom-sheet shells on
// [data-review-drawer] (the write-review form) and
// [data-reviews-filters-drawer] (the filter controls). Both are opened and
// closed via the CustomEvents they export, not called directly.
import { REVIEW_DRAWER_EVENTS } from '@stardust/review-drawer';
import { REVIEW_FILTERS_DRAWER_EVENTS } from '@stardust/review-filters-drawer';
import Slider from '@stardust/slider';
import { SCREENS } from '@stardust/constants';

/** Reviews per page when the section doesn't say — matches the schema default. */
const DEFAULT_REVIEWS_PER_PAGE = 10;

/** Value the Rating dropdown uses for "no rating filter". */
const RATING_FILTER_ALL = 'all';

/**
 * 4xx statuses that are still worth retrying: a timeout and a rate limit say
 * "not now", not "never".
 */
const TRANSIENT_CLIENT_STATUSES = [408, 429];

/**
 * Whether a failed request was refused outright rather than merely failing.
 *
 * A 4xx means the provider will not serve this request however many times it
 * is asked — a missing endpoint, a rejected parameter, an app key without the
 * feature. Everything else (5xx, a dropped connection, CORS, an abort) is a
 * blip: the status is absent or server-side, and the same request may well
 * work on the next try.
 *
 * @param {Error & { status?: number }} error
 * @returns {boolean}
 */
function isRequestRefused(error) {
  const status = error?.status;
  if (typeof status !== 'number') return false;
  return status >= 400 && status < 500 && !TRANSIENT_CLIENT_STATUSES.includes(status);
}

/** Pages either side of the current one in the numbered pagination strip. */
const PAGINATION_WINDOW = 2;

/**
 * Items shown in the "Reviews with media" strip. The strip scrolls sideways,
 * so this is a ceiling on page weight rather than a count anyone sees — it
 * has to be generous enough that review photos and video both fit, since a
 * tight cap silently drops whichever category is ordered last.
 */
const MEDIA_STRIP_COUNT = 24;

/**
 * Album items requested. Much larger than what the strip shows, because the
 * album is filtered down hard: most of it is Instagram photos, which the
 * strip skips. Asking only for what fits truncated the album before reaching
 * its review photos and video.
 */
const MEDIA_STRIP_ALBUM_FETCH = 50;

/**
 * Reviews pulled to fill the strip. Most reviews with photos have exactly
 * one, so this is deliberately larger than MEDIA_STRIP_COUNT — asking for ten
 * reviews would usually yield ten photos, but a run of multi-photo reviews
 * would fill the strip from two or three of them and hide the rest.
 */
const MEDIA_STRIP_REVIEW_COUNT = 25;

/**
 * Slots held back for album media when review photos would otherwise take
 * every one.
 *
 * Needed because the two sources are capped together: most reviews with
 * media have a single photo, so MEDIA_STRIP_REVIEW_COUNT of them fills
 * MEDIA_STRIP_COUNT on its own and whichever source is concatenated second
 * is cut entirely. Video exists only in the album, so that cut removes the
 * one thing the merge is for. Either source still takes the whole strip when
 * the other has nothing to put in it.
 */
const MEDIA_STRIP_ALBUM_SLOTS = 8;

/**
 * Add `?reviews_debug=1` to a product URL to have the media strip report what
 * each Yotpo source returned and how the two were merged. The strip is built
 * from two endpoints that disagree with each other, so when the result looks
 * wrong the only useful question is which source a given item came from —
 * and that is invisible in the finished markup.
 * @returns {boolean}
 */
function mediaStripDebugEnabled() {
  try {
    return new URLSearchParams(window.location.search).has('reviews_debug');
  } catch {
    return false;
  }
}

/**
 * Where the write-review form switches from the bottom sheet to the desktop
 * dialog. The nav breakpoint, the same one the filter bar and filter sheet
 * switch at, so a tablet never gets the desktop form beside the mobile filters.
 */
const DESKTOP_MEDIA_QUERY = `(min-width: ${SCREENS.nav}px)`;

/** Keys that step through the write-review star rating, per the WAI-ARIA radio group pattern. */
const RATING_KEY_STEPS = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

/** The word shown beside the stars for each rating in the write-review form. */
const RATING_WORDS = { 1: 'Poor', 2: 'Fair', 3: 'Average', 4: 'Good', 5: 'Excellent' };

/** How long the success message stays visible before the write-review shell auto-closes. */
const REVIEW_SUCCESS_CLOSE_DELAY_MS = 2000;

/**
 * Clamp on a review body before "Read more" appears. Whether the toggle is
 * needed is measured after layout, not guessed from the text length — the
 * column is far wider on desktop than on a phone, so the same review wraps to
 * a different number of lines at each size. Written out in full so Tailwind's
 * source scanner finds it.
 */
const REVIEW_BODY_CLAMP_CLASS = 'line-clamp-3';

/** Dims the list while a filter, sort or page load is in flight. */
const LIST_LOADING_CLASS = 'opacity-50';

/**
 * A row of review photos: slides sideways when it outgrows its column. Shared
 * by the per-review rows in the list and the "Reviews with media" strip in
 * the summary panel, so both drag and key the same way.
 *
 * Every photo is a button, so arrow keys move focus between them rather than
 * adding a tab stop on the row. No prev/next arrows: drag, swipe and keys
 * cover it, and the photo cut off at the edge shows there's more.
 *
 * Free-scrolling, with the browser's own momentum: snapping on release made
 * the row impossible to swipe away from its end, because Slider treats "at
 * the end" as "the last item is current" and then scrolls straight back to
 * it. These rows have no page or slide to land on — a photo part-way out of
 * view is a fine resting place.
 */
const REVIEW_IMAGES_SLIDER_OPTIONS = { snap: false, snapOnRelease: false, rootTabStop: false, focusFollowsArrows: true };

/** Pending or loaded photo popup module; see loadReviewMediaPopup(). */
let reviewMediaPopupModule = null;

/**
 * Fetch the photo popup's module on demand. The reviews section usually sits
 * far down the page, so a static import would download the popup with it even
 * though it's only needed once someone taps a review photo. A failed fetch is
 * forgotten so the next tap retries.
 * @returns {Promise<typeof import('./review-media-popup.js')>}
 */
function loadReviewMediaPopup() {
  reviewMediaPopupModule ??= import('@stardust/review-media-popup').catch((error) => {
    reviewMediaPopupModule = null;
    throw error;
  });
  return reviewMediaPopupModule;
}

// ─── UI Utilities ────────────────────────────────────────────────────────────

/**
 * The " · date" tail of a question or answer byline, or nothing when the
 * record has no usable date — so a missing date drops the separator too
 * rather than leaving a dangling dot.
 * @param {string} createdAt
 * @returns {string}
 */
function bylineDate(createdAt) {
  const formatted = formatLongDate(createdAt);
  return formatted ? ` · ${formatted}` : '';
}

/**
 * Calculate star distribution percentages from a page of reviews.
 * Used as a fallback when the full distribution is not available.
 * @param {Array} reviews - Array of review objects with score property
 * @returns {Object} Object with keys 1-5 containing count and percentage
 */
function calculateStarDistribution(reviews) {
  const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

  reviews.forEach(review => {
    const score = Math.round(review.score);
    if (score >= 1 && score <= 5) distribution[score]++;
  });

  const total = reviews.length || 1;

  return Object.fromEntries(
    Object.entries(distribution).map(([star, count]) => [
      star, { count, percentage: Math.round((count / total) * 100) }
    ])
  );
}

/**
 * Compute a weighted average rating from a full star distribution.
 * More accurate than averaging a single page of reviews.
 * @param {{ 1: number, 2: number, 3: number, 4: number, 5: number }} dist
 * @returns {number}
 */
function weightedAverageFromDistribution(dist) {
  const total = [1, 2, 3, 4, 5].reduce((sum, s) => sum + dist[s], 0);
  if (total === 0) return 0;
  return [1, 2, 3, 4, 5].reduce((sum, s) => sum + s * dist[s], 0) / total;
}

/** Keys that move focus between tabs, per the WAI-ARIA tabs pattern. */
const TAB_KEY_STEPS = { ArrowRight: 1, ArrowLeft: -1 };

/**
 * Reviews / Q&A tab switcher. The tablist is server-rendered hidden (so the
 * no-JS page is just the reviews panel) and revealed here. Activation follows
 * focus; arrow keys, Home and End move between tabs with a roving tabindex.
 */
class ReviewsTabs {
  /**
   * @param {HTMLElement} container
   * @param {{ onSelect?: (tabName: string) => void }} [options] - Called with
   *   the newly selected tab's `data-reviews-tab` value.
   */
  constructor(container, { onSelect } = {}) {
    this.container = container;
    this.onSelect = onSelect;
    this.container.addEventListener('click', (e) => {
      const tab = e.target.closest('[data-reviews-tab]');
      if (tab) this.select(tab);
    });
    this.container.addEventListener('keydown', (e) => this.handleKeydown(e));
    this.reveal();
  }

  get tabs() {
    return [...this.container.querySelectorAll('[data-reviews-tab]')];
  }

  /** Un-hide the tablist. Re-run after a morph, which restores the Liquid `hidden`. */
  reveal() {
    this.container.querySelector('[data-reviews-tablist]')?.removeAttribute('hidden');
  }

  /**
   * Put back the tab the shopper had open. morph.js restores the server's
   * aria-selected/hidden — Reviews selected — so without this a morph while
   * Q&A is open silently switches tabs and leaves focus on one that is no
   * longer selected. Run after the panels' own morph handling, since
   * selecting Q&A starts its load.
   */
  restoreSelection() {
    const tab = this.tabs.find((candidate) => candidate.dataset.reviewsTab === this.selectedTabName);
    if (tab && tab.getAttribute('aria-selected') !== 'true') this.select(tab);
  }

  select(activeTab) {
    this.selectedTabName = activeTab.dataset.reviewsTab;
    this.tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
      this.container.querySelector(`[data-reviews-panel="${tab.dataset.reviewsTab}"]`)?.toggleAttribute('hidden', !isActive);
    });
    this.onSelect?.(activeTab.dataset.reviewsTab);
  }

  handleKeydown(e) {
    const current = e.target.closest('[data-reviews-tab]');
    if (!current) return;

    const tabs = this.tabs;
    let next;
    if (e.key in TAB_KEY_STEPS) {
      const index = (tabs.indexOf(current) + TAB_KEY_STEPS[e.key] + tabs.length) % tabs.length;
      next = tabs[index];
    } else if (e.key === 'Home') {
      next = tabs[0];
    } else if (e.key === 'End') {
      next = tabs[tabs.length - 1];
    }
    if (!next) return;

    e.preventDefault();
    this.select(next);
    next.focus();
  }
}

/**
 * Q&A panel: questions with their public answers, a page at a time behind a
 * "Show more" button (the provider's page size is fixed).
 *
 * Two-step load: probeCount() fetches only the counts (a tiny request) so the
 * tab can show its count; the questions themselves load the first time the
 * tab is opened, replacing the server-rendered skeleton. ProductReviews
 * decides when to probe (once the section nears the viewport).
 */
class ProductQuestions {
  constructor(container, { productId }) {
    this.container = container;
    this.productId = productId;
    this.page = 1;
    /** Count from the probe; null until it resolves (or if it failed). */
    this.totalQuestions = null;
    /** Product whose questions are currently rendered, so re-opening the tab doesn't refetch. */
    this.loadedProductId = null;
    this._loadGeneration = 0;
    this._countGeneration = 0;

    this.container.addEventListener('click', (e) => {
      if (e.target.closest('[data-qa-more]')) this.load({ append: true });
    });
  }

  get listEl() { return this.container.querySelector('[data-qa-list]'); }
  get emptyEl() { return this.container.querySelector('[data-qa-empty]'); }
  get countEl() { return this.container.querySelector('[data-qa-count]'); }

  /**
   * Re-sync after a section morph. The morph restores the Liquid skeleton and
   * empty count, so reset (lazy-load again on next open) when the product
   * changed or the list is back to its loading state.
   * @param {string} productId
   * @returns {boolean} Whether state was reset — the caller re-probes the count.
   */
  handleSectionMorph(productId) {
    const productChanged = productId !== this.productId;
    if (!productChanged && this.listEl?.getAttribute('aria-busy') !== 'true') return false;
    this.productId = productId;
    this.totalQuestions = null;
    this.loadedProductId = null;
    this._loadGeneration++; // drop any in-flight load for the old markup
    this._countGeneration++; // and any in-flight count for the old product
    return true;
  }

  async probeCount() {
    const generation = ++this._countGeneration;
    try {
      const { totalQuestions } = await fetchQuestionCount({ productId: this.productId });
      if (generation !== this._countGeneration) return;
      this.totalQuestions = totalQuestions;
      this.renderCount();
    } catch (error) {
      // Non-fatal: the tab just shows no count, and opening it still loads.
      if (generation === this._countGeneration) console.warn('Failed to load question count:', error);
    }
  }

  renderCount() {
    if (this.countEl) this.countEl.textContent = this.totalQuestions > 0 ? `(${this.totalQuestions})` : '';
  }

  /** Called when the Q&A tab is selected — loads the first page once per product. */
  open() {
    if (this.loadedProductId === this.productId) return;

    // The probe already says there's nothing to fetch.
    if (this.totalQuestions === 0) {
      this.render({ questions: [], totalQuestions: 0, hasMore: false }, { append: false });
      this.loadedProductId = this.productId;
      return;
    }

    this.load();
  }

  async load({ append = false } = {}) {
    const generation = ++this._loadGeneration;
    const productId = this.productId;
    const page = append ? this.page + 1 : 1;
    const moreBtn = this.container.querySelector('[data-qa-more]');
    const moreErrorEl = this.container.querySelector('[data-qa-more-error]');
    if (moreBtn) moreBtn.disabled = true;
    moreErrorEl?.setAttribute('hidden', '');

    try {
      const data = await fetchQuestions({ productId, page });
      if (generation !== this._loadGeneration) return; // superseded by a newer load

      this.page = data.page;
      this.totalQuestions = data.totalQuestions;
      this.loadedProductId = productId;
      this.render(data, { append });
    } catch (error) {
      if (generation !== this._loadGeneration) return;
      console.error('Failed to load questions:', error);
      if (append) {
        if (moreBtn) moreBtn.disabled = false;
        moreErrorEl?.removeAttribute('hidden');
      } else if (this.listEl) {
        // loadedProductId stays unset, so re-opening the tab retries.
        this.listEl.innerHTML = '<p class="py-8 text-center text-gray-500">Failed to load questions</p>';
        this.listEl.removeAttribute('aria-busy');
      }
    }
  }

  render({ questions, totalQuestions, hasMore }, { append }) {
    const listEl = this.listEl;
    if (!listEl) return;

    this.renderCount();

    const isEmpty = totalQuestions === 0;
    this.emptyEl?.classList.toggle('hidden', !isEmpty);
    listEl.classList.toggle('hidden', isEmpty);

    this.container.querySelector('[data-qa-more]')?.closest('[data-qa-more-wrapper]')?.remove();
    const itemsHtml = questions.map((question) => this.renderQuestion(question)).join('');
    const previousCount = listEl.children.length;
    if (append) {
      listEl.insertAdjacentHTML('beforeend', itemsHtml);
    } else {
      listEl.innerHTML = itemsHtml;
    }
    listEl.removeAttribute('aria-busy');

    // "Show more" was just removed with focus on it. Hand focus to the first
    // question it brought in, which is also where the shopper reads next.
    if (append) {
      const firstNew = listEl.children[previousCount];
      if (firstNew) {
        firstNew.tabIndex = -1;
        firstNew.focus({ preventScroll: true });
      }
    }

    if (hasMore) {
      listEl.insertAdjacentHTML('afterend', `
        <div class="mt-6 text-center" data-qa-more-wrapper>
          <button type="button" class="button-secondary px-4 py-2 text-sm" data-qa-more>Show more questions</button>
          <p class="mt-2 text-sm text-red-700" role="alert" hidden data-qa-more-error>
            Couldn't load more questions. Please try again.
          </p>
        </div>
      `);
    }
  }

  renderQuestion({ id, content, createdAt, author, answers }) {
    return `
      <article class="py-6 space-y-4" data-question-id="${escapeHtml(String(id))}">
        <div>
          <h4 class="font-semibold text-gray-900">Q: ${escapeHtml(content ?? '')}</h4>
          <p class="mt-1 text-sm text-gray-500">${escapeHtml(author.displayName)}${bylineDate(createdAt)}</p>
        </div>
        ${answers.map((answer) => this.renderAnswer(answer)).join('')}
      </article>
    `;
  }

  renderAnswer({ content, createdAt, author, isStoreOwner }) {
    return `
      <div class="border-l-2 border-gray-200 pl-4">
        <p class="text-base text-gray-600 whitespace-pre-line">${escapeHtml(content ?? '')}</p>
        <p class="mt-1 text-sm text-gray-500">
          ${escapeHtml(author.displayName)}${isStoreOwner ? ' <span class="font-medium text-gray-700">(Store owner)</span>' : ''}${bylineDate(createdAt)}
        </p>
      </div>
    `;
  }
}

export default class ProductReviews {
  constructor(container, { productId }) {
    this.container = container;
    this.productId = productId;

    this.cacheElements();

    // The filter controls exist twice — the inline bar and the mobile sheet —
    // so these are collections, and every write goes to all of them. The
    // sheet lives outside the width box but inside the section, so the
    // container query still reaches it.
    // Queried and bound in bindFilterControls(), which re-runs after a morph
    // replaces this markup.
    this.ratingFilterEls = [];
    this.mediaFilterEls = [];
    this.starFilterEls = [];

    this.bindReviewShellChrome();

    // State
    this.reviews = [];
    /**
     * The unfiltered first page init() fetched. The summary falls back to it
     * when no source supplied a distribution — never to this.reviews, which a
     * filter or page change may have replaced with a narrower set.
     */
    this.summaryFallbackReviews = [];
    this.starDistribution = null;
    this.totalReviewCount = 0;
    /**
     * The review metafields' count and average, when Liquid passed them (a
     * product with more than a page of reviews). They outrank the API's for
     * the summary; read fresh by init() for each product.
     * @type {{ totalReviews: number, averageScore: number }|null}
     */
    this.metafieldSummary = null;
    /** Reviews matching the current filters, across all pages. */
    this.listTotal = 0;
    this.currentPage = 1;
    this.perPage = parseInt(container.dataset.reviewsPerPage, 10) || DEFAULT_REVIEWS_PER_PAGE;
    this.totalPages = 1;
    this.currentSort = 'date';
    this.currentDirection = 'desc';
    this.currentStarFilter = null;
    /** Whether the list is limited to reviews with photos (the With Media toggle). */
    this.mediaOnly = false;
    /**
     * Set once the provider refuses a media-filtered request, which takes the
     * toggle off the page for the rest of the visit rather than leaving a
     * control that fails every time it is pressed.
     */
    this.mediaFilterUnavailable = false;
    /** @type {Slider|null} Drag/keyboard scrolling for the "Reviews with media" row. */
    this.mediaStripSlider = null;
    /** @type {Object[]} Popup slides backing the media strip, in display order. */
    this.mediaStripSlides = [];
    /**
     * Bumped on every hydrateMediaStrip() call. The strip has its own counter
     * rather than sharing _loadGeneration, which the sort and page controls
     * also bump — those don't rebuild the strip, and borrowing their counter
     * would make an in-flight hydrate abandon a strip it had already cleared.
     */
    this._mediaStripGeneration = 0;
    /** @type {Slider[]} One per review's photo row; rebuilt with the list. */
    this.imageSliders = [];
    // Bumped at the start of every init()/fetchAndRender() call so a slower
    // response for a since-superseded load (e.g. the previous product on a
    // combined-listing morph, or a rapid double sort/page click) can detect
    // it's stale and bail instead of overwriting newer data. This one guards
    // the LIST, which depends on the current filter.
    this._loadGeneration = 0;
    // Bumped only by init(). Guards what depends on the product alone — the
    // summary and distribution — so a list reload can't cancel them.
    this._initGeneration = 0;

    this.bindEvents();
    this.bindVariantChange();

    // Q&A tab — only present when the section's "Show Q&A tab" setting is on.
    if (container.querySelector('[data-reviews-tablist]')) {
      this.questions = new ProductQuestions(container, { productId });
      this.tabs = new ReviewsTabs(container, {
        onSelect: (tabName) => {
          this.showSummaryForTab(tabName);
          if (tabName === 'qa') this.questions.open();
        }
      });
    }

    // The section sits near the bottom of the PDP, so hold every Yotpo
    // request (reviews, bottomline, Q&A count) until it approaches the
    // viewport — most visitors never scroll that far.
    this.hasStarted = false;
    /**
     * What has been fetched and drawn for the current product, tracked per
     * part because the parts arrive independently: the summary and
     * distribution come from init(), the list from init() or from any later
     * sort, rating, media or page load — and either can land first. A
     * same-product morph can only repaint what exists, and has to leave the
     * placeholders of the rest alone; start() or the pending load fills them.
     */
    this.summaryLoaded = false;
    this.listLoaded = false;
    whenNearViewport(container, () => this.start());
  }

  /**
   * Look up the section's own nodes.
   *
   * Runs from the constructor and again at the start of every morph. A morph
   * diffs the live section against the server's markup and patches nodes in
   * place — but where the two disagree structurally it replaces nodes, or
   * repurposes one into its neighbour's role. A reference taken once would
   * then be detached, or be a different element altogether, and the render
   * methods would draw reviews where nobody can see them while the real list
   * kept its skeletons. Looking them up again is cheap and always right.
   *
   * Nothing may be bound directly to one of these nodes for the same reason:
   * listeners are delegated on the container, which survives a morph.
   */
  cacheElements() {
    const { container } = this;
    this.summaryCardEl = container.querySelector('[data-reviews-summary-card]');
    this.avgScoreEl = container.querySelector('[data-average-score]');
    this.starsEl = container.querySelector('[data-stars]');
    this.totalReviewsEls = container.querySelectorAll('[data-total-reviews-count]');
    this.distributionEl = container.querySelector('[data-reviews-distribution]');
    this.distributionBars = Object.fromEntries(
      [1, 2, 3, 4, 5].map(star => [star, {
        bar: container.querySelector(`[data-star-bar="${star}"]`),
        count: container.querySelector(`[data-star-count="${star}"]`),
      }])
    );
    this.listEl = container.querySelector('[data-reviews-list]');
    this.paginationEl = container.querySelector('[data-reviews-pagination]');
    this.emptyStateEl = container.querySelector('[data-reviews-empty]');
    this.noResultsEl = container.querySelector('[data-reviews-no-results]');
  }

  // The write-review shells sit outside the section element but inside the
  // section's morph root, so a morph can touch them too. They are looked up
  // from the document each time rather than held — see cacheElements().
  get reviewFormTemplate() { return document.querySelector('[data-review-form-template]'); }
  get questionFormTemplate() { return document.querySelector('[data-question-form-template]'); }
  get reviewDialogEl() { return document.querySelector('[data-review-dialog]'); }
  get reviewDrawerEl() { return document.querySelector('[data-review-drawer]'); }

  /** First load, once the section nears the viewport. Uses the current productId, so a pre-start morph is picked up. */
  start() {
    this.hasStarted = true;
    this.init();
    this.questions?.probeCount();
  }

  /**
   * Keep the container's image dataset in sync with the selected variant.
   * Unlike productId/title/url, the image is variant-level and never touched
   * by handleSectionMorph (that only fires on a combined-listing product
   * swap), so a plain same-product variant switch left it stuck on the
   * page-load variant — the submit handlers read it straight off the
   * container.
   *
   * The variant SKU is deliberately not tracked: nothing submits it. Yotpo
   * identifies a product by its Shopify id (see yotpoProductKey in yotpo.js).
   */
  bindVariantChange() {
    document.addEventListener('variant:change', (e) => {
      const variant = e.detail?.variant;
      if (!variant) return;
      if (variant.product_id != null && String(variant.product_id) !== String(this.productId)) return;

      const imageUrl = variant.featured_image?.src || variant.featured_media?.src;
      if (imageUrl) this.container.dataset.productImage = imageUrl;
    });
  }

  /**
   * Re-sync productId from the (morphed-in-place) container and reload the
   * widget for it. Called by the shared theme:section:morph listener at the
   * bottom of this file. The reviews reload only when the product actually
   * changed; the Q&A tabs re-sync on every morph, since it restores their
   * server-rendered markup. Before start() has run, nothing is fetched —
   * start() picks up the new productId itself.
   *
   * Combined-listing swaps morph this section in place — without this,
   * this.productId is only ever read once at construction, left stale: the
   * review list keeps showing the old product and a write-review submit
   * would attach to it too, even though submitReview()'s other fields
   * (sku/title/url/image) are read live off the container and would already
   * reflect the new product.
   */
  handleSectionMorph() {
    // First, before anything below reads a node: the morph may have replaced
    // or repurposed them. See cacheElements().
    this.cacheElements();

    // morph.js keeps an open <dialog> open (it copies `open` across) but
    // swaps the form inside it for the server's empty slot, which would leave
    // the shopper in a blank modal over the page. review-drawer.js resets the
    // mobile sheet the same way; this is the desktop counterpart. It applies
    // whether or not the product changed, since the slot is emptied either way.
    const dialog = this.reviewDialogEl;
    if (dialog?.open) this.closeReviewForm(dialog);

    const newProductId = this.container.dataset.productId;
    if (!newProductId) return;

    // The morph restores the tablist's Liquid `hidden`, the Q&A skeleton and
    // the filter controls, so these re-sync even on a same-product morph.
    this.tabs?.reveal();
    const questionsReset = this.questions?.handleSectionMorph(newProductId);
    if (questionsReset && this.hasStarted) this.questions.probeCount();
    this.tabs?.restoreSelection();
    this.bindFilterControls();

    if (newProductId === this.productId) {
      // Same product, so init() is not going to run — but Liquid has just put
      // the list back to skeletons, the summary to zeros, the sort to its
      // default and the strip to empty, and nothing else would refill them.
      this.repaintLoadedState();
      this.restoreMediaStrip();
      return;
    }

    this.productId = newProductId;
    // What is on screen belongs to the old product until init() redraws it.
    this.summaryLoaded = false;
    this.listLoaded = false;
    this.currentPage = 1;
    this.currentSort = 'date';
    this.currentDirection = 'desc';
    this.currentStarFilter = null;
    // processReviewsData only overwrites these when the new payload has a
    // positive total / a distribution, so a swap onto a zero-review product
    // would otherwise keep showing the previous product's summary numbers.
    this.totalReviewCount = 0;
    this.starDistribution = null;
    // The old product's reviews, which the summary fallback must not average.
    this.reviews = [];
    this.summaryFallbackReviews = [];
    this.listTotal = 0;
    this.mediaOnly = false;
    this.syncSortControl();
    this.syncFilterControls();

    // Not near the viewport yet — start() will load the new product when it is.
    if (this.hasStarted) this.init();
  }

  async init() {
    // Two guards, because two different things can overtake this load:
    //  - `generation` is bumped by every load that touches the list (this one,
    //    and each sort, rating, media or page change), so it answers "is the
    //    list I'd draw still the one the shopper asked for?";
    //  - `initToken` is bumped only here, so it answers "has the product
    //    changed under me?". The summary and distribution depend on that and
    //    nothing else.
    const generation = ++this._loadGeneration;
    const initToken = ++this._initGeneration;

    // Re-runs on a product-changing morph (handleSectionMorph) — clear the
    // previous product's empty state so a swap to one with reviews doesn't
    // leave the "no reviews yet" message stuck on screen.
    this.toggleEmptyState(false);

    // Read per product: a product-changing morph brings the new product's
    // data attributes (or none) along with its id.
    this.metafieldSummary = this.readMetafieldSummary();

    // Started here, not after the reviews land, for two reasons: it fetches
    // for itself so there is nothing to wait for, and a reviews fetch that
    // throws would otherwise skip it entirely — leaving the previous
    // product's thumbnails under the new product's error message, and
    // leaving its own generation unbumped so an in-flight hydrate for the old
    // product could still paint.
    this.hydrateMediaStrip();

    try {
      // Fetched separately from the reviews list: the bottomline (v3) reflects
      // grouped-product totals, while the list (v1) does not — see yotpo.js.
      // Its own total/distribution therefore take precedence over the list's.
      // The two requests are started together but settled separately. They
      // used to share a Promise.all, so a failed list threw away a bottomline
      // response that had succeeded — the summary was lost to a failure that
      // had nothing to do with it.
      const bottomLineRequest = fetchBottomLine({ productId: this.productId }).catch(() => null);
      let listError = null;
      const reviewsData = await fetchReviews({
        productId: this.productId,
        perPage: this.perPage,
        page: this.currentPage,
        sort: this.currentSort,
        direction: this.currentDirection,
      }).catch((error) => {
        listError = error;
        return null;
      });
      const bottomLine = await bottomLineRequest;

      // The product changed while this was in flight; the page now belongs to
      // the newer init(), so drop this response whole.
      if (initToken !== this._initGeneration) return;

      // Fall back to the list's own total whenever bottomline didn't supply a
      // usable one — a failed fetch (bottomLine === null) or a resolved-but-
      // zero total both count, so a bottomline quirk can't zero out an
      // otherwise-accurate count from the reviews list.
      const bottomLineApplied = bottomLine ? this.applySummaryStats(bottomLine) : false;
      if (reviewsData) this.summaryFallbackReviews = reviewsData.reviews;
      if (!bottomLineApplied && reviewsData) {
        this.applySummaryStats({
          totalReviews: reviewsData.pagination.total,
          starDistribution: reviewsData.starDistribution,
        });
      }
      // The metafields' total outranks both; the distribution stays the API's.
      if (this.metafieldSummary) this.totalReviewCount = this.metafieldSummary.totalReviews;

      // Any source is enough to say something true about the product. With
      // none there is nothing to draw, and zeros would be a false answer.
      const hasSummary = this.metafieldSummary !== null || bottomLine !== null || reviewsData !== null;

      // This request was unfiltered and first-page. If a later load has taken
      // over the list (a sort, rating, media or page change made while this
      // was in flight), drawing it now would overwrite what the shopper asked
      // for — but that must not cost the summary its numbers, so the two are
      // decided separately.
      const listIsCurrent = generation === this._loadGeneration;
      if (reviewsData && listIsCurrent) this.processReviewsData(reviewsData);

      if (hasSummary) {
        this.renderSummary();
        this.renderDistribution();
        this.clearSummaryLoadingState();
        this.summaryLoaded = true;
      }

      if (reviewsData) {
        if (listIsCurrent) {
          this.renderReviews();
          this.renderPagination();
          this.toggleEmptyState(this.reviews.length === 0);
          this.listLoaded = true;
        }
      } else {
        console.error('Failed to load reviews:', listError);
        // Only if this call still owns the list: a sort made meanwhile may
        // have drawn real reviews, which an error message would wipe.
        if (listIsCurrent) this.showError('Failed to load reviews');
      }
      if (listIsCurrent) this.setListLoading(false);

      // Nothing at all to show for the summary, and nothing will retry it for
      // this product. Settle it as unavailable rather than leaving the zeroed
      // placeholder marked busy forever — or presenting those zeros as a real
      // "0.0, based on 0 reviews" beside a list that may well have reviews.
      if (!hasSummary) this.renderSummaryUnavailable();
    } catch (error) {
      // Reached only by a bug in the drawing code above: request failures are
      // handled inline. Same ownership rules as before.
      if (initToken !== this._initGeneration) return;
      console.error('Failed to load reviews:', error);
      if (generation === this._loadGeneration) {
        this.showError('Failed to load reviews');
        this.setListLoading(false);
        this.clearSummaryLoadingState();
      }
    }
  }

  /**
   * Mark the list as loading (dimmed, aria-busy) or settled. The two always
   * move together, so assistive tech is told the same thing the dimming shows.
   * @param {boolean} isLoading
   */
  setListLoading(isLoading) {
    this.listEl?.classList.toggle(LIST_LOADING_CLASS, isLoading);
    this.listEl?.toggleAttribute('aria-busy', isLoading);
  }

  /**
   * Say what a filter, sort or page change produced, through the visually
   * hidden status region beside the list. The list itself isn't a live
   * region — re-reading every review on each change would bury the result.
   */
  announceListResult() {
    const statusEl = this.container.querySelector('[data-reviews-status]');
    if (!statusEl) return;
    if (this.listTotal === 0) {
      statusEl.textContent = this.hasActiveFilters ? 'No reviews match these filters' : 'No reviews yet';
      return;
    }
    const pageNote = this.totalPages > 1 ? `, page ${this.currentPage} of ${this.totalPages}` : '';
    statusEl.textContent = `Showing ${pluralizeReviews(this.listTotal)}${pageNote}`;
  }

  /**
   * Move focus to the top of the freshly drawn list. Used after controls that
   * vanish or get rebuilt by the load they started (page buttons, Clear
   * filters), which would otherwise drop keyboard focus to the page body.
   */
  focusList() {
    const target = this.listEl?.classList.contains('hidden')
      ? this.container.querySelector('[data-reviews-empty]:not(.hidden), [data-reviews-no-results]:not(.hidden)')
      : this.listEl;
    if (!target) return;
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  /**
   * Checks the sort radio for the current sort and writes its label into the
   * "Sort: …" summary. Looked up live rather than cached, since a morph can
   * replace the markup.
   */
  syncSortControl() {
    const radio = this.container.querySelector(`[data-reviews-sort][value="${this.currentSort}"]`);
    if (!radio) return;
    radio.checked = true;
    const label = this.container.querySelector('[data-reviews-sort-label]');
    if (label) label.textContent = radio.dataset.sortLabel;
  }

  /**
   * Draw everything a morph just reset, from the state already in hand.
   *
   * A morph diffs the live section against the server's markup, and the
   * server knows nothing about fetched reviews: it sends the skeleton rows,
   * zeroed summary, default sort and an empty strip. With the product
   * unchanged init() does not run, so without this the shopper is left on
   * placeholders until some later filter or reload happens to refetch.
   *
   * Nothing is fetched. Everything drawn here is already held — the current
   * page of reviews, the distribution, the totals. The render methods read
   * the nodes cacheElements() looked up at the top of handleSectionMorph, so
   * this works whether the morph patched them in place or replaced them.
   */
  repaintLoadedState() {
    // The sort radios go back to the server's default; the state is the truth.
    this.syncSortControl();

    if (this.summaryLoaded) {
      this.renderSummary();
      this.renderDistribution();
      this.clearSummaryLoadingState();
    }

    if (this.listLoaded) {
      this.renderReviews();
      this.renderPagination();
      this.toggleEmptyState(this.reviews.length === 0);
      // Liquid put the skeleton's aria-busy back along with the skeleton.
      this.setListLoading(false);
    }
  }

  /**
   * Clear the `aria-busy` set in Liquid on the summary/distribution
   * containers, once their content reflects a final state (success or
   * error) rather than the zero-filled loading markup. Runs after render so
   * assistive tech reads settled content instead of the "0" placeholder.
   */
  clearSummaryLoadingState() {
    this.summaryCardEl?.removeAttribute('aria-busy');
    this.distributionEl?.removeAttribute('aria-busy');
  }

  /**
   * The review metafields' count and average from the section's data
   * attributes. Liquid writes them only for a product with more than a page
   * of reviews (see dynamic-product-reviews.liquid); otherwise null, and the
   * summary comes from the API.
   * @returns {{ totalReviews: number, averageScore: number }|null}
   */
  readMetafieldSummary() {
    const totalReviews = parseInt(this.container.dataset.metafieldReviewCount, 10);
    const averageScore = parseFloat(this.container.dataset.metafieldReviewAverage);
    if (Number.isNaN(totalReviews) || Number.isNaN(averageScore)) return null;
    return { totalReviews, averageScore };
  }

  /**
   * Set totalReviewCount/starDistribution from an accurate summary source —
   * either the bottomline fetch, or (as a fallback) the review list's own
   * pagination total/distribution. Zero is a legitimate totalReviews value
   * for a brand-new product; the guard just avoids clobbering a real count
   * with a missing one, and totalReviewCount already starts at 0.
   * @param {{ totalReviews: number, starDistribution: Object|null }} stats
   * @returns {boolean} Whether a usable (positive) total was applied — callers
   *   use this to decide whether a fallback source is still needed.
   */
  applySummaryStats({ totalReviews, starDistribution }) {
    const applied = totalReviews > 0;
    if (applied) this.totalReviewCount = totalReviews;
    if (starDistribution) this.starDistribution = starDistribution;
    return applied;
  }

  processReviewsData({ reviews, pagination }) {
    this.reviews = reviews;
    this.listTotal = pagination.total;
    this.totalPages = pagination.totalPages;
    this.currentPage = pagination.currentPage;
  }

  bindEvents() {
    // One delegated handler for the controls that live in the server-rendered
    // markup: write-review (summary card and empty state), the distribution
    // rows, and the mobile Filters button.
    this.container.addEventListener('click', (e) => {
      if (e.target.closest('[data-write-review]')) {
        this.openReviewForm();
        return;
      }

      if (e.target.closest('[data-ask-question]')) {
        this.openQuestionForm();
        return;
      }

      if (e.target.closest('[data-reviews-filters-open]')) {
        document.dispatchEvent(new CustomEvent(REVIEW_FILTERS_DRAWER_EVENTS.open));
        return;
      }

      if (e.target.closest('[data-reviews-clear-filters]')) {
        this.clearFilters();
        return;
      }

      const stripTrigger = e.target.closest('[data-reviews-media-strip-trigger]');
      if (stripTrigger) {
        this.openMediaStripItem(stripTrigger);
        return;
      }

      const starBtn = e.target.closest('[data-star-filter]');
      if (starBtn) {
        // Pressing the active row again clears the filter, so the
        // distribution rows aren't a one-way trip with no visible way back.
        const star = parseInt(starBtn.dataset.starFilter, 10);
        this.applyStarFilter(this.currentStarFilter === star ? null : star);
        return;
      }

      // Pagination. Scoped by ancestor so it only ever matches this widget's
      // own page buttons.
      const pageBtn = e.target.closest('[data-reviews-pagination] [data-page]');
      if (pageBtn) {
        const page = parseInt(pageBtn.dataset.page, 10);
        if (page !== this.currentPage && page >= 1 && page <= this.totalPages) {
          // The page buttons are rebuilt by the load, so focus follows the
          // shopper to the new page rather than dropping to the page body.
          this.fetchAndRender({ page }).then((rendered) => { if (rendered) this.focusList(); });
          this.scrollToTop();
        }
        return;
      }

      // Review media and read-more toggles inside the list.
      const mediaTrigger = e.target.closest('[data-reviews-list] [data-review-media-trigger]');
      if (mediaTrigger) {
        this.openReviewMedia(mediaTrigger);
        return;
      }

      const toggleBtn = e.target.closest('[data-reviews-list] [data-review-body-toggle]');
      if (toggleBtn) {
        this.toggleReviewBody(toggleBtn);
      }
    });

    // Sort dropdown: radios in a <details>, matching the collection page's
    // text-style Sort control. Delegated like everything above, so it keeps
    // working if a morph replaces the markup. The rating filters also fire
    // `change` on their way up, hence the closest() check. Picking an option
    // closes the dropdown, as a native select would.
    this.container.addEventListener('change', (e) => {
      const sort = e.target.closest?.('[data-reviews-sort]');
      if (!sort) return;
      this.currentSort = sort.value;
      this.syncSortControl();
      sort.closest('[data-reviews-sort-dropdown]')?.removeAttribute('open');
      this.fetchAndRender();
    });

    this.bindFilterControls();

    // A narrower or wider column rewraps every review, so which ones overflow
    // changes with it. Watched on the container (which survives a morph) and
    // for width only: iOS Safari fires window resize as its toolbar slides
    // during scroll, which changes height alone and rewraps nothing.
    let observedWidth = null;
    new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      if (width === observedWidth) return;
      const isFirstObservation = observedWidth === null;
      observedWidth = width;
      if (!isFirstObservation) this.syncReviewBodyToggles();
    }).observe(this.container);
  }

  /**
   * Load and draw one page of the list for the current sort and filters.
   *
   * The page is an argument rather than set on this.currentPage up front:
   * currentPage only changes once a page has actually drawn (in
   * processReviewsData). Setting it first left a failed load's page marked
   * current, so pressing that page again to retry did nothing.
   *
   * @param {{ page?: number }} [options] - Defaults to the first page, which
   *   is where any sort or filter change starts.
   * @returns {Promise<boolean>} Whether this call drew the list.
   */
  async fetchAndRender({ page = 1 } = {}) {
    const generation = ++this._loadGeneration;
    const wasMediaOnly = this.mediaOnly;
    this.setListLoading(true);
    try {
      const reviewsData = await fetchReviews({
        productId: this.productId,
        perPage: this.perPage,
        page,
        sort: this.currentSort,
        direction: this.currentDirection,
        star: this.currentStarFilter,
        pictured: this.mediaOnly,
      });

      if (generation !== this._loadGeneration) return false; // superseded by a newer load

      this.processReviewsData(reviewsData);
      this.renderReviews();
      this.renderPagination();
      this.toggleEmptyState(this.reviews.length === 0);
      this.listLoaded = true;
      this.announceListResult();
      return true;
    } catch (error) {
      if (generation !== this._loadGeneration) return false;
      console.error('Failed to fetch reviews:', error);

      // Only the media filter routes through a second endpoint, so a failure
      // while it was on is the one case we could attribute to that endpoint —
      // but only when Yotpo actually refused the request. A blip is not
      // grounds for taking a working control off the page for the rest of
      // the visit; the shopper gets the error and can try again.
      if (wasMediaOnly && isRequestRefused(error)) {
        console.warn('Reviews provider refused the With Media filter; hiding the toggle.', error);
        this.disableMediaFilter();
        return this.fetchAndRender();
      }

      this.showError('Failed to load reviews');
      return false;
    } finally {
      // Only the newest load settles the loading state. A superseded call
      // clearing it un-dimmed the list while the load that replaced it was
      // still in flight. Whichever call is newest — this, a later
      // fetchAndRender, or init() — always clears it when it settles, so
      // the list can't be left dimmed.
      if (generation === this._loadGeneration) this.setListLoading(false);
    }
  }

  renderSummary() {
    let averageScore = 0;
    const fallbackReviews = this.summaryFallbackReviews;

    if (this.metafieldSummary) {
      averageScore = this.metafieldSummary.averageScore;
    } else if (this.starDistribution) {
      averageScore = weightedAverageFromDistribution(this.starDistribution);
    } else if (fallbackReviews.length > 0) {
      averageScore = fallbackReviews.reduce((acc, r) => acc + r.score, 0) / fallbackReviews.length;
    }

    const totalReviews = this.totalReviewCount;
    const displayScore = formatScore(averageScore);

    if (this.avgScoreEl) this.avgScoreEl.textContent = displayScore;
    // The score and stars are hidden from assistive tech; this is what it
    // reads. "0.0 out of 5" would sound like a zero rating, not an unrated product.
    const ratingLabelEl = this.summaryCardEl?.querySelector('[data-rating-label]');
    if (ratingLabelEl) {
      ratingLabelEl.textContent = totalReviews === 0 ? 'No reviews yet' : `Rated ${displayScore} out of 5 stars`;
    }
    // Empty stars follow the panel's text color at the same strength as the
    // server-rendered placeholder, so the swap to real data isn't visible as
    // a color change and a dark panel doesn't get light-gray stars.
    if (this.starsEl) {
      this.starsEl.innerHTML = renderStars(averageScore, {
        size: 'w-5 h-5',
        filledColor: STAR_COLOR_CLASS,
        emptyColor: 'text-current opacity-30'
      });
    }
    // The noun is written here with the number, not left in the Liquid
    // sentence, because it has to agree with it: "Based on 1 review".
    this.totalReviewsEls.forEach(el => {
      el.textContent = pluralizeReviews(totalReviews);
    });
  }

  /**
   * Settle the summary when neither source answered. The zeroed placeholder
   * would otherwise either stay busy for good or read as a real "0.0", so the
   * score is replaced with a dash and assistive tech is told plainly.
   */
  renderSummaryUnavailable() {
    if (this.avgScoreEl) this.avgScoreEl.textContent = '–';
    const ratingLabelEl = this.summaryCardEl?.querySelector('[data-rating-label]');
    if (ratingLabelEl) ratingLabelEl.textContent = 'Rating unavailable';
    this.clearSummaryLoadingState();
  }

  renderDistribution() {
    if (!this.distributionEl) return;

    let distribution;

    if (this.starDistribution) {
      // With the metafields' total in totalReviewCount, the bars measure
      // against the distribution's own sum instead: the two can be a review
      // or two apart, and a bar past 100% would overflow its track.
      const distributionSum = Object.values(this.starDistribution).reduce((a, b) => a + b, 0);
      const total = (this.metafieldSummary ? distributionSum : this.totalReviewCount || distributionSum) || 1;
      distribution = Object.fromEntries(
        [1, 2, 3, 4, 5].map(star => [star, {
          count: this.starDistribution[star],
          percentage: Math.round((this.starDistribution[star] / total) * 100)
        }])
      );
    } else if (this.summaryFallbackReviews.length) {
      distribution = calculateStarDistribution(this.summaryFallbackReviews);
    } else {
      return;
    }

    for (let star = 5; star >= 1; star--) {
      const { bar, count } = this.distributionBars[star];
      if (bar) bar.style.width = `${distribution[star].percentage}%`;
      if (count) count.textContent = distribution[star].count;

      // The row's visible content is a bare numeral, a star and a count, which
      // reads as "5 261" on its own — so the whole row is named here instead,
      // now that the count is known.
      const total = distribution[star].count;
      this.distributionEl
        .querySelector(`[data-star-filter="${star}"]`)
        ?.setAttribute('aria-label', `Show only ${star} star reviews (${total})`);
    }
  }

  renderReviews() {
    if (!this.listEl) return;

    this.destroyImageSliders();

    if (this.reviews.length === 0) {
      this.listEl.innerHTML = '';
      return;
    }

    this.listEl.innerHTML = this.reviews.map(review => this.renderReviewItem(review)).join('');
    this.imageSliders = [...this.listEl.querySelectorAll('[data-review-images]')]
      .map(row => new Slider(row, REVIEW_IMAGES_SLIDER_OPTIONS));

    this.syncReviewBodyToggles();

    // Photos are on screen, so fetch the popup now and a tap opens it without
    // waiting on the network. A failure here is retried on that tap.
    if (this.imageSliders.length) loadReviewMediaPopup().catch(() => {});
  }

  /**
   * Initials for the reviewer avatar: first letters of the first two words,
   * so "Sandra C." reads SC and "Alyssa" reads A.
   * @param {string} displayName
   * @returns {string}
   */
  reviewerInitials(displayName) {
    return (displayName || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0].toUpperCase())
      .join('');
  }

  /**
   * One review, as a single full-width column: reviewer, stars, title, body,
   * then media. The 16px rhythm between blocks and the 32px below the last
   * one come straight from the design.
   *
   * The reviewer's row is an avatar of their initials, their name with
   * "Verified Buyer" under it, and a short date pinned right. The date is
   * short here (and long in the photo popup) because this row has a name and
   * a badge competing for the same line.
   */
  renderReviewItem(review) {
    const { id, score, author, title, content, createdAt, verifiedBuyer, media } = review;
    const name = author.displayName;

    return `
      <article class="border-b border-gray-200 pt-6 pb-8 last:border-b-0" data-review-id="${escapeHtml(String(id))}">
        <div class="flex items-start gap-3">
          <span
            class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-current text-sm font-semibold"
            aria-hidden="true"
          >${escapeHtml(this.reviewerInitials(name))}</span>
          <div class="min-w-0 flex-1">
            <p class="text-base font-medium wrap-break-word">${escapeHtml(name)}</p>
            ${verifiedBuyer ? '<p class="text-sm opacity-70">Verified Buyer</p>' : ""}
          </div>
          <p class="shrink-0 text-sm opacity-70">${formatShortDate(createdAt)}</p>
        </div>

        <div class="mt-4 flex items-center gap-0.5">${renderStars(score, { filledColor: STAR_COLOR_CLASS })}</div>

        <div class="mt-4">
          ${title ? `<h4 class="font-semibold">${escapeHtml(title)}</h4>` : ""}
          ${this.renderReviewBody(content)}
        </div>

        ${media?.length ? this.renderReviewMedia(media, name) : ""}
      </article>
    `;
  }

  /**
   * A review's text, clamped by REVIEW_BODY_CLAMP_CLASS.
   *
   * The toggle ships hidden and syncReviewBodyToggles() reveals it only on
   * the reviews whose text is genuinely clipped. It can't be decided here:
   * nothing has been laid out yet, so there is no way to know how many lines
   * the text will take.
   */
  renderReviewBody(content) {
    return `
      <div data-review-body>
        <p class="text-base opacity-80 ${REVIEW_BODY_CLAMP_CLASS}" data-review-body-text>${escapeHtml(content ?? '')}</p>
        <button
          type="button"
          class="mt-1 hidden cursor-pointer text-sm font-medium hover:underline active:underline"
          data-review-body-toggle
          aria-expanded="false"
        >
          Read more
        </button>
      </div>
    `;
  }

  /**
   * Show "Read more" only where the text is actually clipped.
   *
   * Measured rather than guessed from the character count, which is what the
   * old version did: at 300 characters a review gets a toggle whether or not
   * it overflows, so on the wide single-column layout a three-line review
   * showed a button that clipped nothing and did nothing when pressed.
   *
   * Runs after a render and again on resize, since the same text wraps to a
   * different number of lines as the column changes width. Already-expanded
   * reviews are skipped — their text isn't clamped, so it would measure as
   * not overflowing and the toggle would vanish mid-read.
   */
  syncReviewBodyToggles() {
    requestAnimationFrame(() => {
      // Every measurement first, then every class change. Interleaving them
      // forced a fresh layout for each review in the list.
      const measured = [...(this.listEl?.querySelectorAll('[data-review-body]') ?? [])]
        .map((wrapper) => ({
          textEl: wrapper.querySelector('[data-review-body-text]'),
          toggle: wrapper.querySelector('[data-review-body-toggle]'),
        }))
        .filter(({ textEl, toggle }) => textEl && toggle && toggle.getAttribute('aria-expanded') !== 'true')
        // Sub-pixel line heights make these differ by a fraction even when
        // nothing is clipped, so compare with a pixel of slack.
        .map(({ textEl, toggle }) => ({ toggle, isClipped: textEl.scrollHeight > textEl.clientHeight + 1 }));

      measured.forEach(({ toggle, isClipped }) => toggle.classList.toggle('hidden', !isClipped));
    });
  }

  toggleReviewBody(toggleBtn) {
    const textEl = toggleBtn.closest('[data-review-body]')?.querySelector('[data-review-body-text]');
    if (!textEl) return;

    const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
    textEl.classList.toggle(REVIEW_BODY_CLAMP_CLASS, isExpanded);
    toggleBtn.setAttribute('aria-expanded', String(!isExpanded));
    toggleBtn.textContent = isExpanded ? 'Read more' : 'Read less';
  }

  renderVerifiedBadge() {
    return `
      <div class="inline-flex items-center gap-1 mt-2">
        <svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
          <path fill-rule="evenodd" d="M12 2c-.791 0-1.55.314-2.11.874l-.893.893a.985.985 0 0 1-.696.288H7.04A2.984 2.984 0 0 0 4.055 7.04v1.262a.986.986 0 0 1-.288.696l-.893.893a2.984 2.984 0 0 0 0 4.22l.893.893a.985.985 0 0 1 .288.696v1.262a2.984 2.984 0 0 0 2.984 2.984h1.262c.261 0 .512.104.696.288l.893.893a2.984 2.984 0 0 0 4.22 0l.893-.893a.985.985 0 0 1 .696-.288h1.262a2.984 2.984 0 0 0 2.984-2.984V15.7c0-.261.104-.512.288-.696l.893-.893a2.984 2.984 0 0 0 0-4.22l-.893-.893a.985.985 0 0 1-.288-.696V7.04a2.984 2.984 0 0 0-2.984-2.984h-1.262a.985.985 0 0 1-.696-.288l-.893-.893A2.984 2.984 0 0 0 12 2Zm3.683 7.73a1 1 0 1 0-1.414-1.413l-4.253 4.253-1.277-1.277a1 1 0 0 0-1.415 1.414l1.985 1.984a1 1 0 0 0 1.414 0l4.96-4.96Z" clip-rule="evenodd"/>
        </svg>
        <span class="text-sm font-medium text-green-600">Verified Buyer</span>
      </div>
    `;
  }

  /**
   * A review's photos and videos as buttons that open the popup at that item.
   * Everything shows; renderReviews() makes the row a Slider (see
   * REVIEW_IMAGES_SLIDER_OPTIONS), and without it the row still scrolls
   * natively. The row's padding keeps focus rings clear of the scroll clip; its
   * negative margin keeps the media aligned with the review text.
   * @param {import('./yotpo.js').NormalizedReviewMedia[]} media
   * @param {string} authorName
   */
  renderReviewMedia(media, authorName) {
    const safeAuthor = escapeHtml(authorName);
    return `
      <div class="mt-4" data-review-images>
        <ul class="-m-1 flex gap-2 overflow-x-auto p-1" role="list" data-slider-scroll-target>
          ${media.map((item, index) => `
            <li class="shrink-0" data-slider-item>
              <button
                type="button"
                class="relative block h-24 w-24 overflow-hidden rounded-lg hover:opacity-80 active:opacity-80 transition-opacity"
                data-review-media-trigger="${index}"
                aria-haspopup="dialog"
                aria-label="View ${item.isVideo ? 'video' : 'photo'} ${index + 1} of ${media.length} from ${safeAuthor}'s review"
              >
                <img
                  src="${escapeHtml(item.thumbUrl)}"
                  alt=""
                  class="h-full w-full object-cover"
                  loading="lazy"
                  width="96"
                  height="96"
                />
                ${item.isVideo ? renderPlayBadge('h-7 w-7') : ''}
              </button>
            </li>
          `).join('')}
        </ul>
      </div>
    `;
  }

  /**
   * Open the photo popup on the tapped photo, paging through that review's photos.
   * @param {HTMLElement} trigger - A [data-review-media-trigger] button
   */
  async openReviewMedia(trigger) {
    const reviewId = trigger.closest('[data-review-id]')?.dataset.reviewId;
    const review = this.reviews.find((r) => String(r.id) === reviewId);
    if (!review?.media?.length) return;

    try {
      const { openReviewMediaPopup } = await loadReviewMediaPopup();
      openReviewMediaPopup({
        slides: this.buildMediaSlides(review),
        index: Number(trigger.dataset.reviewMediaTrigger),
        trigger,
      });
    } catch (error) {
      console.error('Review photo popup failed to load:', error);
    }
  }

  /**
   * One popup slide per photo, each carrying the review's details. Kept
   * per-review so a future "reviews with media" strip can concatenate
   * several reviews' slides into one popup.
   * @param {Object} review - A normalized review from the provider
   * @returns {import('./review-media-popup.js').ReviewMediaSlide[]}
   */
  buildMediaSlides(review) {
    const { headerHtml, bodyHtml } = this.renderMediaDetails(review);
    const total = review.media.length;
    const author = review.author.displayName;

    return review.media.map((item, index) => ({
      // A video's "image" is its poster frame.
      imageUrl: item.fullUrl,
      thumbUrl: item.thumbUrl,
      alt: `${item.isVideo ? 'Video' : 'Photo'} ${index + 1} of ${total} from ${author}'s review`,
      reviewId: String(review.id),
      headerHtml,
      bodyHtml,
      ...(item.videoUrl ? { videoUrl: item.videoUrl } : {}),
    }));
  }

  /**
   * Review details for the photo popup: the same pieces as a review in the
   * list, with the full body (no "Read more" clamp) and colors from the
   * popup's scheme variables instead of fixed grays. Split in two because the
   * popup puts its thumbnail row between them.
   *
   * Every field but the author is optional, because the media strip's photos
   * come from the UGC album rather than the review list: some carry no review
   * at all, and their date can be missing. Rendering those unconditionally
   * printed "Invalid Date" (or 1 January 1970) and an empty five-star row.
   *
   * @param {Object} details
   * @param {{ displayName: string }} details.author
   * @param {number} [details.score]
   * @param {string} [details.title]
   * @param {string} [details.content]
   * @param {string} [details.createdAt]
   * @param {boolean} [details.verifiedBuyer]
   * @returns {{ headerHtml: string, bodyHtml: string }}
   */
  renderMediaDetails({ score, author, title, content, createdAt, verifiedBuyer }) {
    const headerHtml = `
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0">
          <p class="text-base font-semibold wrap-break-word">${escapeHtml(author.displayName)}</p>
          ${verifiedBuyer ? this.renderVerifiedBadge() : ''}
        </div>
        ${createdAt ? `<p class="shrink-0 text-sm text-[var(--review-popup-muted)]">${formatLongDate(createdAt)}</p>` : ''}
      </div>
      ${score ? `<div class="mt-4 flex items-center gap-1">${renderStars(score, { size: 'w-5 h-5', filledColor: STAR_COLOR_CLASS })}</div>` : ''}
    `;
    const bodyHtml = `
      ${title ? `<h3 class="mt-6 text-lg font-semibold text-[var(--review-popup-title)]">${escapeHtml(title)}</h3>` : ''}
      ${content ? `<p class="${title ? 'mt-2' : 'mt-6'} text-base whitespace-pre-line wrap-break-word">${escapeHtml(content)}</p>` : ''}
    `;
    return { headerHtml, bodyHtml };
  }

  /**
   * The page numbers to show: a window either side of the current page,
   * clamped to the ends and always the same width so the strip doesn't
   * change size as the shopper pages through.
   * @returns {number[]}
   */
  paginationPages() {
    const span = PAGINATION_WINDOW * 2 + 1;
    const count = Math.min(span, this.totalPages);
    let first = this.currentPage - PAGINATION_WINDOW;
    first = Math.max(1, Math.min(first, this.totalPages - count + 1));
    return Array.from({ length: count }, (_, i) => first + i);
  }

  /**
   * Numbered pagination. Colors come from the section scheme via
   * currentColor, with the current page marked by the panel's button colors —
   * so the strip follows the scheme without naming any color of its own.
   */
  renderPagination() {
    if (!this.paginationEl || this.totalPages <= 1) {
      if (this.paginationEl) this.paginationEl.innerHTML = '';
      return;
    }

    const arrow = (page, label, path, disabled) => `
      <li>
        <button
          type="button"
          class="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full transition-opacity hover:opacity-60 active:opacity-60 disabled:cursor-not-allowed disabled:opacity-30 motion-reduce:transition-none"
          data-page="${page}"
          aria-label="${label}"
          ${disabled ? 'disabled' : ''}
        >
          <svg class="h-4 w-4" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${path}" />
          </svg>
        </button>
      </li>
    `;

    const numbers = this.paginationPages().map((page) => {
      const isCurrent = page === this.currentPage;
      return `
        <li>
          <button
            type="button"
            class="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-sm transition-colors motion-reduce:transition-none ${
              isCurrent
                ? 'bg-[var(--reviews-panel-button-bg)] font-medium text-[var(--reviews-panel-button-text)]'
                : 'hover:opacity-60 active:opacity-60'
            }"
            data-page="${page}"
            aria-label="Page ${page}"
            ${isCurrent ? 'aria-current="page"' : ''}
          >
            ${page}
          </button>
        </li>
      `;
    }).join('');

    this.paginationEl.innerHTML = `
      <nav aria-label="Reviews pagination">
        <ul class="flex items-center justify-center gap-1" role="list">
          ${arrow(this.currentPage - 1, 'Previous page', 'm15 19-7-7 7-7', this.currentPage === 1)}
          ${numbers}
          ${arrow(this.currentPage + 1, 'Next page', 'm9 5 7 7-7 7', this.currentPage === this.totalPages)}
        </ul>
      </nav>
    `;
  }

  /**
   * Render the media strip from slides already in hand.
   *
   * Split from the fetch so a morph can put the row back without asking Yotpo
   * again: Liquid re-renders the strip empty and hidden on every morph, and a
   * same-product variant switch has exactly the same media.
   *
   * @param {import('./review-media-popup.js').ReviewMediaSlide[]} slides
   */
  paintMediaStrip(slides) {
    const listEl = this.mediaStripEl?.querySelector('[data-reviews-media-strip-list]');
    if (!listEl || !slides.length) return;

    this.mediaStripSlides = slides;

    // basis-2/5 puts two thumbnails and part of a third in view at any
    // width, so the row reads as scrollable without a fixed pixel size that
    // would show a different number of them on a phone than in the 320px
    // desktop column.
    listEl.innerHTML = slides.map((slide, index) => `
      <li class="shrink-0 basis-2/5" data-slider-item>
        <button
          type="button"
          class="relative block w-full cursor-pointer overflow-hidden rounded-lg transition-opacity hover:opacity-80 active:opacity-80 motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring-color)]"
          data-reviews-media-strip-trigger="${index}"
          aria-haspopup="dialog"
          aria-label="${escapeHtml(slide.alt)}"
        >
          <img
            src="${escapeHtml(slide.thumbUrl)}"
            alt=""
            class="aspect-square w-full object-cover"
            loading="lazy"
            decoding="async"
            width="128"
            height="128"
          />
          ${slide.videoUrl ? renderPlayBadge() : ''}
        </button>
      </li>
    `).join('');
    this.mediaStripEl?.classList.remove('hidden');

    // Photos are on screen, so fetch the popup now and a tap opens it
    // without waiting on the network. A failure here is retried on that tap.
    loadReviewMediaPopup().catch(() => {});

    // Built after the items exist — Slider reads [data-slider-item] once, at
    // construction. Any previous one is dropped first: after a morph it holds
    // document-level drag listeners pointing at replaced nodes.
    this.mediaStripSlider?.destroy();
    const sliderRoot = this.mediaStripEl?.querySelector('[data-reviews-media-strip-slider]');
    this.mediaStripSlider = sliderRoot ? new Slider(sliderRoot, REVIEW_IMAGES_SLIDER_OPTIONS) : null;
  }

  /**
   * Put the strip back after a morph re-rendered it empty and hidden.
   *
   * Re-queries its element first, since a morph can replace the node rather
   * than patch it. Nothing fetches here — the media is per product, not per
   * variant, so a same-product morph repaints what was already loaded; a
   * product change goes through init() and hydrateMediaStrip() instead.
   */
  restoreMediaStrip() {
    this.paintMediaStrip(this.mediaStripSlides);
  }

  /**
   * Fill the "Reviews with media" strip in the summary panel.
   *
   * Photos come from the product's UGC album rather than the current page of
   * reviews, so the strip shows the product's photos as a whole and doesn't
   * change as the shopper filters or pages. The strip stays hidden when there
   * are none, so it never leaves a labelled empty row.
   *
   * Failure is silent: the strip is decoration, and the reviews it sits
   * beside have already rendered.
   */
  async hydrateMediaStrip() {
    const listEl = this.mediaStripEl?.querySelector('[data-reviews-media-strip-list]');
    if (!listEl) return;

    const productId = this.productId;
    const generation = ++this._mediaStripGeneration;

    // Hide and clear first: init() re-runs on a combined-listing morph, and
    // the previous product's photos would otherwise sit under the new
    // product's reviews until (or unless) this fetch replaced them. The
    // slider goes with them — its document-level drag listeners would
    // otherwise stay attached to detached nodes.
    this.mediaStripSlider?.destroy();
    this.mediaStripSlider = null;
    this.mediaStripSlides = [];
    this.mediaStripEl?.classList.add('hidden');
    listEl.innerHTML = '';

    try {
      // Both sources, because neither is complete on its own — see
      // mergeMediaStripSlides. Each is allowed to fail without taking the
      // strip down with it. The review fetch is newest-first and deliberately
      // not tied to the list's sort or filter controls: the strip is a
      // gallery of the product's media, not a view of the page below it.
      // A failing source is survivable — the other still fills the strip —
      // but it is never silent: losing every video because the album 404'd
      // looks identical to the product having no video.
      const [album, pictured] = await Promise.all([
        fetchUGCImages({ productId, count: MEDIA_STRIP_ALBUM_FETCH }).catch((error) => {
          console.warn('[reviews] media strip: album fetch failed, continuing without it:', error);
          return { images: [] };
        }),
        fetchReviews({ productId, perPage: MEDIA_STRIP_REVIEW_COUNT, page: 1, pictured: true })
          .catch((error) => {
            console.warn('[reviews] media strip: pictured-review fetch failed, continuing without it:', error);
            return { reviews: [] };
          }),
      ]);
      // Superseded while in flight. Both checks earn their place:
      //  - the generation catches an A -> B -> A swap, where the first
      //    request for A lands after a later hydrate has already cleared the
      //    row and the product id on its own still matches;
      //  - the product id catches a swap whose reload failed before reaching
      //    this method, so nothing bumped the generation.
      // Keeping both means the guard holds wherever this is called from.
      if (generation !== this._mediaStripGeneration || productId !== this.productId) return;

      // Already capped, and capped per source — slicing again here is what
      // let one source crowd out the other.
      const slides = this.mergeMediaStripSlides(album.images, pictured.reviews);
      this.debugMediaStrip({ productId, album: album.images, reviews: pictured.reviews, slides });
      if (!slides.length) return;

      this.paintMediaStrip(slides);
    } catch (error) {
      console.warn('Failed to load the reviews media strip:', error);
    }
  }

  /**
   * Report how the media strip was assembled, behind `?reviews_debug=1`.
   * Prints each source as it arrived and the merged result, so a strip that
   * looks wrong can be traced to the source it came from — see
   * mediaStripDebugEnabled.
   * @param {{productId: string, album: Object[], reviews: Object[], slides: Object[]}} data
   */
  debugMediaStrip({ productId, album, reviews, slides }) {
    if (!mediaStripDebugEnabled()) return;

    const short = (url) => String(url || '').replace(/^https?:\/\//, '').split('?')[0].split('/').slice(-3).join('/');

    console.group(`[reviews] media strip — product ${productId}`);

    console.log(`Album returned ${album.length} item(s) — asked for ${MEDIA_STRIP_ALBUM_FETCH}`);
    console.table(album.map((item) => {
      const playable = item.isVideo ? Boolean(item.videoUrl) : Boolean(item.imageUrl || item.thumbUrl);
      let usedAs = 'skipped: social photo';
      if (!playable) usedAs = 'skipped: nothing to show';
      else if (item.reviewImageId) usedAs = 'review photo';
      else if (item.isVideo) usedAs = 'video';
      return {
        usedAs,
        isVideo: item.isVideo,
        reviewId: item.review?.id ?? '—',
        reviewImageId: item.reviewImageId ?? '—',
        thumb: short(item.imageUrl || item.thumbUrl),
      };
    }));

    console.log(`Pictured reviews returned ${reviews.length} review(s) — asked for ${MEDIA_STRIP_REVIEW_COUNT}`);
    console.table(reviews.map((review) => ({
      reviewId: review.id,
      author: review.author?.displayName,
      mediaIds: (review.media || []).map((m) => `${m.isVideo ? 'video:' : ''}${m.id}`).join(', '),
    })));

    const videos = slides.filter((slide) => slide.videoUrl).length;
    console.log(`Strip shows ${slides.length} of a possible ${MEDIA_STRIP_COUNT} slide(s); ${videos} video(s)`);
    console.table(slides.map((slide) => ({
      isVideo: Boolean(slide.videoUrl),
      reviewId: slide.reviewId,
      alt: slide.alt,
      thumb: short(slide.thumbUrl),
    })));

    if (!album.length) console.warn('Album was empty — no video can appear, since it is the only source that has any.');
    if (album.length && !videos) {
      console.warn('The album had items but no video reached the strip — check the usedAs column above.');
    }

    console.groupEnd();
  }

  /**
   * Build the media strip from Yotpo's two media sources at once.
   *
   * Neither alone is right. The product album is what Yotpo's own gallery
   * shows — it is the only source carrying video, and it includes Instagram
   * posts as well as review photos — but it drops review photos that the
   * review list has. The review list is authoritative for review media and
   * carries the full review text, but has no video and no social posts.
   * Checked on live products: each source held a review photo the other was
   * missing, so merging is the only way to show everything.
   *
   * Album order leads, matching Yotpo, with review-only photos appended.
   * Duplicates are dropped on the id the two sources share (see the
   * provider's parseReviewImageId); an album photo that belongs to a review
   * we fetched is upgraded to that review's full detail, so the popup reads
   * the same whichever source a photo arrived from.
   *
   * @param {Object[]} albumItems - Normalized UGC media (photos, video, social)
   * @param {Object[]} reviews - Normalized reviews that have photos
   * @returns {import('./review-media-popup.js').ReviewMediaSlide[]}
   */
  mergeMediaStripSlides(albumItems = [], reviews = []) {
    const reviewsById = new Map(reviews.map((review) => [String(review.id), review]));
    /** Review photo ids the album already covers, so they aren't added twice. */
    const coveredPhotoIds = new Set();
    /** Review photos only the album has. */
    const albumReviewSlides = [];
    /** Video the reviews didn't already supply. */
    const albumVideoSlides = [];

    // Videos the pictured reviews already carry. Photos de-duplicate the
    // other way round — the album copy wins and the review's is dropped —
    // but a video has to be matched before the album is walked, because the
    // keys live on the review side. A review video published to the album
    // arrives with Kaltura URLs and so has no reviewImageId to match on; it
    // would otherwise be appended a second time.
    const coveredVideoKeys = new Set();
    reviews.forEach((review) => {
      (review.media || []).forEach((item) => {
        if (!item.isVideo) return;
        if (item.entryId) coveredVideoKeys.add(item.entryId);
        if (item.galleryMediaId) coveredVideoKeys.add(item.galleryMediaId);
      });
    });

    albumItems.forEach((item) => {
      // A video with no playable source would get a play badge and then open
      // a still, so drop it rather than promise something that can't play.
      if (!(item.isVideo ? item.videoUrl : item.imageUrl || item.thumbUrl)) return;

      const fullReview = item.review?.id ? reviewsById.get(String(item.review.id)) : null;

      if (item.reviewImageId) {
        coveredPhotoIds.add(String(item.reviewImageId));
        albumReviewSlides.push(this.buildAlbumSlide(item, fullReview));
      } else if (item.isVideo) {
        if (coveredVideoKeys.has(item.videoEntryId) || coveredVideoKeys.has(String(item.id))) return;
        albumVideoSlides.push(this.buildAlbumSlide(item, fullReview));
      }
      // Everything else in the album is an Instagram photo. Deliberately
      // skipped: this block is "Reviews with media", and on a real product
      // the social photos outnumbered the review ones 8 to 2 and pushed
      // every actual review photo out of the strip.
    });

    // Built per review so each slide keeps its review's id and the popup's
    // thumbnail row still groups correctly.
    const reviewSlides = reviews.flatMap((review) =>
      this.buildMediaSlides(review).filter((_, i) => !coveredPhotoIds.has(String(review.media[i]?.id)))
    );

    // Video first within the album's share — it is the only content neither
    // review endpoint can supply, so it should never be the piece that falls
    // off the end.
    const albumSlides = [...albumVideoSlides, ...albumReviewSlides];

    // Review media leads, newest first, so the strip opens on the same photos
    // as the list below it. The album keeps a reserved share rather than
    // taking whatever is left, and each side expands into the other's unused
    // slots, so the strip is always as full as the sources allow.
    const reserved = Math.min(albumSlides.length, MEDIA_STRIP_ALBUM_SLOTS);
    const reviewTake = Math.min(reviewSlides.length, MEDIA_STRIP_COUNT - reserved);
    const albumTake = Math.min(albumSlides.length, MEDIA_STRIP_COUNT - reviewTake);

    return [...reviewSlides.slice(0, reviewTake), ...albumSlides.slice(0, albumTake)];
  }

  /**
   * One album item as a popup slide.
   * @param {Object} item - A normalized UGC media item
   * @param {Object|null} review - The full review it belongs to, when we have one
   * @returns {import('./review-media-popup.js').ReviewMediaSlide}
   */
  buildAlbumSlide(item, review) {
    const { headerHtml, bodyHtml } = this.renderMediaDetails(
      review ?? {
        author: item.author,
        score: item.review?.score,
        title: item.review?.title,
        content: item.review?.content,
        createdAt: item.createdAt,
      }
    );

    // The review's own name beats the album's, which falls back to a generic
    // "Customer" when the album item carries no reviewer details.
    const author = review?.author?.displayName || item.author?.displayName;
    const kind = item.isVideo ? 'video' : 'photo';

    return {
      // A video's "image" is its poster: Yotpo gives no still to blow up.
      imageUrl: item.isVideo
        ? (item.imageUrl || item.thumbUrl)
        : (item.originalImageUrl || item.thumbUrl || item.imageUrl),
      thumbUrl: item.imageUrl || item.thumbUrl,
      alt: author ? `Customer ${kind} from ${author}` : `Customer ${kind}`,
      // Social posts have no review to group with, so each stands alone in
      // the popup's thumbnail row.
      reviewId: String(item.review?.id ?? `ugc-${item.id}`),
      headerHtml,
      bodyHtml,
      ...(item.isVideo && item.videoUrl ? { videoUrl: item.videoUrl } : {}),
    };
  }

  /**
   * Open the photo popup on a tapped media-strip thumbnail.
   * @param {HTMLElement} trigger - A [data-reviews-media-strip-trigger] button
   */
  async openMediaStripItem(trigger) {
    if (!this.mediaStripSlides.length) return;

    try {
      const { openReviewMediaPopup } = await loadReviewMediaPopup();
      // The whole strip goes in, not just the tapped photo, so the popup's
      // arrows browse the row the shopper was already looking at. Each slide
      // carries its own review id, which the popup groups its thumbnail row
      // by — so that row still shows one review's photos at a time.
      openReviewMediaPopup({
        slides: this.mediaStripSlides,
        index: Number(trigger.dataset.reviewsMediaStripTrigger),
        trigger,
      });
    } catch (error) {
      console.error('Review photo popup failed to load:', error);
    }
  }

  /**
   * Query the filter controls and bind them.
   *
   * Re-run after a combined-listing morph, which re-renders this markup from
   * Liquid: the nodes held from before are then either detached or, where
   * disableMediaFilter() removed them, replaced by fresh unbound ones. An
   * AbortController drops the previous bindings first, so a morph that
   * patched a control in place doesn't end up with two handlers on it.
   */
  bindFilterControls() {
    this._filterAbort?.abort();
    this._filterAbort = new AbortController();
    const { signal } = this._filterAbort;

    this.ratingFilterEls = [...this.container.querySelectorAll('[data-reviews-rating-filter]')];
    this.mediaFilterEls = [...this.container.querySelectorAll('[data-reviews-media-filter]')];
    // Bound by delegation on the container, so these are only read for
    // syncFilterControls().
    this.starFilterEls = [...this.container.querySelectorAll('[data-star-filter]')];

    // A provider that refused the media filter once will refuse it again, so
    // take the toggle back off a section whose markup was just restored —
    // otherwise the morph quietly hands back a control that can't work.
    if (this.mediaFilterUnavailable) {
      this.mediaFilterEls.forEach((btn) => btn.remove());
      this.mediaFilterEls = [];
    }

    // The Rating dropdown and the distribution rows are two ways of setting
    // the same filter; both land in applyStarFilter, which re-syncs whichever
    // control the shopper didn't touch.
    this.ratingFilterEls.forEach((select) => {
      select.addEventListener('change', (e) => {
        const value = e.target.value;
        this.applyStarFilter(value === RATING_FILTER_ALL ? null : parseInt(value, 10));
      }, { signal });
    });

    this.mediaFilterEls.forEach((btn) => {
      btn.addEventListener('click', () => this.applyMediaFilter(!this.mediaOnly), { signal });
    });

    this.syncFilterControls();
  }

  /** Drop every filter and reload — the way out of a no-results state. */
  clearFilters() {
    this.currentStarFilter = null;
    this.mediaOnly = false;
    this.syncFilterControls();
    // The Clear filters button hides once results come back, which would
    // drop focus to the page body.
    this.fetchAndRender().then((rendered) => { if (rendered) this.focusList(); });
  }

  /**
   * Set the rating filter and reload. `null` clears it.
   * @param {number|null} star
   */
  applyStarFilter(star) {
    this.currentStarFilter = star;
    this.syncFilterControls();
    this.fetchAndRender();
  }

  /**
   * Turn the With Media filter on or off and reload.
   * @param {boolean} enabled
   */
  applyMediaFilter(enabled) {
    if (this.mediaFilterUnavailable) return;
    this.mediaOnly = enabled;
    this.syncFilterControls();
    this.fetchAndRender();
  }

  /**
   * Push the current filter state onto every control that shows it: both
   * copies of each filter bar (inline and mobile sheet) and the distribution
   * rows. One state, several views — so a change made in the sheet is already
   * reflected in the bar when the viewport crosses the breakpoint.
   */
  syncFilterControls() {
    const ratingValue = this.currentStarFilter == null ? RATING_FILTER_ALL : String(this.currentStarFilter);
    this.ratingFilterEls.forEach((select) => { select.value = ratingValue; });

    this.mediaFilterEls.forEach((btn) => {
      btn.setAttribute('aria-checked', String(this.mediaOnly));
    });

    this.starFilterEls.forEach((btn) => {
      btn.setAttribute('aria-pressed', String(parseInt(btn.dataset.starFilter, 10) === this.currentStarFilter));
    });
  }

  /**
   * Take the With Media toggle off the page for the rest of the visit. Called
   * when the provider refuses a media-filtered request — the endpoint behind
   * it is separate from the one serving the unfiltered list, so it can be
   * unavailable on a store where everything else works.
   */
  disableMediaFilter() {
    this.mediaFilterUnavailable = true;
    this.mediaOnly = false;
    this.mediaFilterEls.forEach((btn) => btn.remove());
    this.mediaFilterEls = [];
    this.syncFilterControls();
  }

  /**
   * The "Reviews with media" strip, looked up live rather than cached.
   *
   * A morph re-renders this markup and can replace the node instead of
   * patching it, so a reference taken once in the constructor may be
   * detached by the time the next hydrate paints into it — thumbnails would
   * land on a node nobody can see while the live strip stayed empty and
   * hidden. The container itself survives a morph, so querying from it is
   * always current.
   * @returns {HTMLElement|null}
   */
  get mediaStripEl() {
    return this.container.querySelector('[data-reviews-media-strip]');
  }

  /**
   * Show or hide the summary box for the tab being opened.
   *
   * Everything the box holds — the score, the stars, the count, the
   * distribution, the media strip and the write-review button — is about
   * reviews, and none of it means anything beside a list of questions. The
   * box is hidden rather than emptied so the Q&A tab doesn't get a blank
   * panel taking up the column; the inner wrapper stays so a Q&A action can
   * be dropped in later without unpicking this.
   *
   * @param {string} tabName - The `data-reviews-tab` value now selected
   */
  showSummaryForTab(tabName) {
    const isReviews = tabName === 'reviews';
    const askBtn = this.container.querySelector('[data-ask-question]');

    this.container.querySelector('[data-reviews-review-summary]')?.classList.toggle('hidden', !isReviews);
    askBtn?.classList.toggle('hidden', isReviews);
    // The box stays up on Q&A for the Ask a question button. With no such
    // button there is nothing left in it, so it goes too rather than leaving
    // an empty panel holding the column open.
    this.summaryCardEl?.classList.toggle('hidden', !isReviews && !askBtn);
  }

  /** Whether the list is narrowed by a rating or media filter right now. */
  get hasActiveFilters() {
    return this.currentStarFilter !== null || this.mediaOnly;
  }

  /**
   * Show exactly one of: the list, "no reviews yet", or "nothing matches
   * these filters".
   *
   * The two empty messages are not interchangeable. A product with reviews
   * where the 2-star filter matches none is not a product without reviews,
   * and the "Write the first review" button in the real empty state would be
   * nonsense beside a summary reading 312 reviews.
   *
   * Toggles every way, so none of the three is left behind by the next call.
   * @param {boolean} isEmpty
   */
  toggleEmptyState(isEmpty) {
    const filteredOut = isEmpty && this.hasActiveFilters;
    this.listEl?.classList.toggle('hidden', isEmpty);
    this.emptyStateEl?.classList.toggle('hidden', !isEmpty || filteredOut);
    this.noResultsEl?.classList.toggle('hidden', !filteredOut);
  }

  /**
   * Tear down the photo-row sliders before anything overwrites the list. The
   * rows being replaced take their sliders with them, but the window/document
   * listeners those sliders hold would stay attached to detached nodes.
   */
  destroyImageSliders() {
    this.imageSliders.forEach(slider => slider.destroy());
    this.imageSliders = [];
  }

  showError(message) {
    if (!this.listEl) return;

    // The error goes into the list, so the list has to be the thing on
    // screen. A previous filter that matched nothing would otherwise have
    // left it hidden behind an empty-state message, and the error would be
    // written somewhere nobody can see while that stale copy stayed up.
    this.toggleEmptyState(false);

    this.destroyImageSliders();
    this.listEl.innerHTML = `
      <div class="py-8 text-center opacity-70">
        <p>${escapeHtml(message)}</p>
      </div>
    `;
    const statusEl = this.container.querySelector('[data-reviews-status]');
    if (statusEl) statusEl.textContent = message;
  }

  scrollToTop() {
    this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ─── Write a review ────────────────────────────────────────────────────

  /**
   * Bind the write-review shells' close handling (the header and form close
   * buttons on both, backdrop-click-to-close on the dialog) once at
   * construction. Binding it per-open, as openReviewForm used to, stacked a
   * duplicate handler on every open.
   */
  bindReviewShellChrome() {
    // Delegated on the document, once. The shells sit outside the container
    // but inside the section's morph root, so a morph can replace them; a
    // listener bound to the shell itself would be left on a detached node and
    // the close button would silently stop working. The form's own Cancel
    // button is inside a shell too, so this covers it as well.
    //
    // A click's target is where the pointer was released, so a text selection
    // dragged out of a field and let go over the backdrop also "clicks" the
    // <dialog> — closing it and losing the typed review. Only a press that
    // also started on the backdrop counts.
    let pressStartedOnShell = false;
    document.addEventListener('pointerdown', (e) => {
      pressStartedOnShell = e.target.matches?.('[data-review-dialog]') ?? false;
    });

    document.addEventListener('click', (e) => {
      const shell = e.target.closest?.('[data-review-dialog], [data-review-drawer]');
      if (!shell) return;

      if (e.target.closest('[data-close-modal]')) {
        this.closeReviewForm(shell);
      } else if (e.target === shell && shell.tagName === 'DIALOG' && pressStartedOnShell) {
        // A click on the backdrop lands on the <dialog> element itself.
        this.closeReviewForm(shell);
      }
    });
  }

  /**
   * Clone a form template into whichever shell suits the viewport and open
   * it: a native <dialog> on desktop, the bottom sheet on mobile.
   *
   * Shared by the write-review and ask-a-question forms — they differ only in
   * their template, their heading and how a submit is handled, so the shell
   * plumbing (clearing stale clones, the close button, picking desktop or
   * mobile) lives here once.
   *
   * @param {Object} options
   * @param {HTMLTemplateElement|null} options.template
   * @param {string} options.formSelector - Identifies the form inside the clone
   * @param {string} options.title - Heading for the shell
   * @param {(shell: HTMLElement, form: HTMLFormElement) => void} options.wire
   */
  openFormShell({ template, formSelector, title, wire }) {
    if (!template) return;

    // A previous submission's auto-close is still pending — cancel it so it
    // can't fire against this freshly (re)opened form a moment later.
    clearTimeout(this._reviewSuccessTimeoutId);

    const isDesktop = window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
    const shell = isDesktop ? this.reviewDialogEl : this.reviewDrawerEl;
    if (!shell) return;

    // Where focus goes back to when the dialog closes. BaseDrawer does this
    // for the mobile sheet; the native <dialog> is handled in closeReviewForm.
    this._formOpener = document.activeElement;

    // Clear every shell's slot, not just the one we're about to open into —
    // otherwise a clone left in the other shell from before a desktop/mobile
    // breakpoint change keeps its review-*-{{ section.id }} ids in the
    // document, colliding with the fresh clone below.
    [this.reviewDialogEl, this.reviewDrawerEl].forEach((s) => {
      const otherSlot = s?.querySelector('[data-review-form-slot]');
      if (otherSlot) otherSlot.innerHTML = '';
    });

    const heading = shell.querySelector('[data-form-shell-title]');
    if (heading) heading.textContent = title;

    const slot = shell.querySelector('[data-review-form-slot]');
    slot.appendChild(template.content.cloneNode(true));

    const form = slot.querySelector(formSelector);
    wire(shell, form);

    if (isDesktop) {
      shell.showModal();
    } else {
      document.dispatchEvent(new CustomEvent(REVIEW_DRAWER_EVENTS.open));
    }
  }

  openReviewForm() {
    this.openFormShell({
      template: this.reviewFormTemplate,
      formSelector: '[data-review-form]',
      title: this.container.dataset.reviewFormTitle || 'Share your thoughts',
      wire: (shell, form) => this.wireReviewForm(shell, form),
    });
  }

  /** Open the ask-a-question form, titled with the product it is about. */
  openQuestionForm() {
    const product = this.container.dataset.productTitle;
    this.openFormShell({
      template: this.questionFormTemplate,
      formSelector: '[data-question-form]',
      title: product ? `Ask a question about ${product}` : 'Ask a question',
      wire: (shell, form) => {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.submitQuestion(shell, form);
        });
      },
    });
  }

  /**
   * Send a question through the provider.
   *
   * Yotpo publishes it only after the asker confirms by email, so the success
   * copy promises a mail rather than a posted question.
   * @param {HTMLElement} shell
   * @param {HTMLFormElement} form
   */
  async submitQuestion(shell, form) {
    const errorEl = form.querySelector('[data-question-form-error]');
    errorEl.classList.add('hidden');

    // Every field here is a real control, so the browser's own validation
    // covers it — unlike the review form, whose star rating it cannot see.
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const data = new FormData(form);
    await this.submitForm({
      shell,
      form,
      errorEl,
      submitBtn: form.querySelector('[data-question-submit]'),
      send: () => createQuestion({
        ...this.productSubmissionDetails(),
        content: data.get('content'),
        displayName: data.get('display_name'),
        email: data.get('email'),
      }),
      successMessage: 'Thanks! Check your email to confirm your question — it goes live once you do.',
      logLabel: 'Failed to send question:',
    });
  }

  /** The product fields every submission carries, read live off the container. */
  productSubmissionDetails() {
    const { productTitle, productUrl, productImage } = this.container.dataset;
    return {
      productId: this.productId,
      productTitle,
      productUrl,
      productImageUrl: productImage,
    };
  }

  /**
   * The send / success / failure steps the review and question forms share.
   *
   * On success the form is replaced by a status message that takes focus, so
   * a screen reader announces it and keyboard focus isn't dropped to the page
   * body along with the submit button it was on. On failure the provider's
   * message (always shopper-facing — see postSubmission in yotpo.js) is shown
   * and the button re-enabled for another try.
   *
   * @param {Object} options
   * @param {HTMLElement} options.shell
   * @param {HTMLFormElement} options.form
   * @param {HTMLElement} options.errorEl
   * @param {HTMLButtonElement} options.submitBtn
   * @param {() => Promise<void>} options.send
   * @param {string} options.successMessage
   * @param {string} options.logLabel
   */
  async submitForm({ shell, form, errorEl, submitBtn, send, successMessage, logLabel }) {
    submitBtn.disabled = true;
    submitBtn.classList.add(LIST_LOADING_CLASS);

    try {
      await send();

      const message = document.createElement('p');
      message.className = 'py-8 text-center';
      message.setAttribute('role', 'status');
      message.tabIndex = -1;
      message.textContent = successMessage;
      form.replaceChildren(message);
      message.focus();
      this._reviewSuccessTimeoutId = setTimeout(() => this.closeReviewForm(shell), REVIEW_SUCCESS_CLOSE_DELAY_MS);
    } catch (error) {
      console.error(logLabel, error);
      errorEl.textContent = error.message;
      errorEl.classList.remove('hidden');
      submitBtn.disabled = false;
      submitBtn.classList.remove(LIST_LOADING_CLASS);
    }
  }

  /**
   * Close whichever write-review shell is open.
   * @param {HTMLElement} shell
   */
  closeReviewForm(shell) {
    clearTimeout(this._reviewSuccessTimeoutId);
    if (shell.tagName === 'DIALOG') {
      shell.close();
      // Not every browser in support returns focus from a closed <dialog>
      // to what opened it, so it is done here. The opener may have been
      // replaced by a morph while the form was up; then there is nothing to
      // return to.
      if (this._formOpener?.isConnected) this._formOpener.focus();
    } else {
      document.dispatchEvent(new CustomEvent(REVIEW_DRAWER_EVENTS.close));
    }
  }

  /**
   * Wire star-rating selection and submit handling for a freshly-cloned review form.
   * @param {HTMLElement} shell
   * @param {HTMLFormElement} form
   */
  wireReviewForm(shell, form) {
    const ratingButtons = [...form.querySelectorAll('[data-rating]')];
    const ratingInput = form.querySelector('[data-rating-input]');
    const ratingWord = form.querySelector('[data-rating-word]');

    const selectRating = (btn) => {
      const value = parseInt(btn.dataset.rating, 10);
      ratingInput.value = value;

      ratingButtons.forEach((other) => {
        const star = parseInt(other.dataset.rating, 10);
        // Radiogroup semantics: exactly one star is "checked", even though
        // every star up to it is filled — so the two are tracked apart.
        other.setAttribute('aria-checked', String(star === value));
        other.toggleAttribute('data-filled', star <= value);
        // Roving tabindex: the group is one tab stop, on the chosen star.
        other.tabIndex = other === btn ? 0 : -1;
      });

      if (ratingWord) ratingWord.textContent = RATING_WORDS[value] ?? '';
    };

    ratingButtons.forEach((btn) => {
      btn.addEventListener('click', () => selectRating(btn));
    });

    // Arrow keys move and select together, as in a native radio group.
    form.querySelector('[data-rating-select]')?.addEventListener('keydown', (e) => {
      const step = RATING_KEY_STEPS[e.key];
      if (!step) return;
      const current = ratingButtons.indexOf(e.target.closest('[data-rating]'));
      if (current === -1) return;
      e.preventDefault();
      const next = ratingButtons[(current + step + ratingButtons.length) % ratingButtons.length];
      selectRating(next);
      next.focus();
    });

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submitReview(shell, form);
    });
  }

  /**
   * Submit the write-review form via the Yotpo provider's createReview().
   * @param {HTMLElement} shell
   * @param {HTMLFormElement} form
   */
  async submitReview(shell, form) {
    const errorEl = form.querySelector('[data-review-form-error]');
    const submitBtn = form.querySelector('[data-review-submit]');
    errorEl.classList.add('hidden');

    const data = new FormData(form);
    const rating = parseInt(data.get('rating'), 10);
    if (!rating) {
      errorEl.textContent = 'Please select a star rating.';
      errorEl.classList.remove('hidden');
      return;
    }

    // The rating is a hidden input, which the constraint validation API
    // ignores entirely (hidden inputs are barred from constraint
    // validation), hence the manual check above. Every other field is a
    // real control, so the form's `novalidate` (added to suppress the
    // browser's automatic validation-on-submit, which would run before our
    // rating check above) doesn't block calling the API directly —
    // reportValidity() still shows the native inline message on the first
    // invalid field.
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    await this.submitForm({
      shell,
      form,
      errorEl,
      submitBtn,
      send: () => createReview({
        ...this.productSubmissionDetails(),
        score: rating,
        title: data.get('title'),
        content: data.get('content'),
        displayName: data.get('display_name'),
        email: data.get('email'),
      }),
      successMessage: 'Thanks! Your review has been submitted and will appear after moderation.',
      logLabel: 'Failed to submit review:',
    });
  }
}

// ─── Auto-initialize ─────────────────────────────────────────────────────────
// Guard against DOMContentLoaded having already fired (Shopify block injection
// can add scripts after the event fires). The compact [data-pdp-reviews]
// block initializes itself in pdp-reviews.js.

/** The page's single ProductReviews instance, if the full section is present. */
let currentProductReviews = null;

function initAll() {
  const reviewsContainer = document.querySelector('[data-product-reviews]');
  if (reviewsContainer?.dataset.productId) {
    currentProductReviews = new ProductReviews(reviewsContainer, { productId: reviewsContainer.dataset.productId });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

// Combined-listing option changes morph the product section in place
// (assets/product.js #morphSectionsForCombinedListing). The reviews widget
// re-syncs only when its own markup was re-rendered: running it for a buy-box
// morph rebuilt the media strip for nothing — throwing away its slider and
// the row's scroll position — and re-ran work that only makes sense against
// freshly server-rendered markup.
document.addEventListener('theme:section:morph', (event) => {
  const reviewsContainer = currentProductReviews?.container;
  if (reviewsContainer && event.target.contains?.(reviewsContainer)) {
    currentProductReviews.handleSectionMorph();
  }
});
